// Serea cart helper: adds lines and keeps Horizon's cart drawer in sync
// by dispatching the theme's own CartLinesUpdateEvent.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

// Re-broadcast any cart line change (add/remove/qty from anywhere) as a simple event.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const p = /** @type {any} */ (event).promise;
  if (!p) return;
  p.then(() => document.dispatchEvent(new CustomEvent('serea:cart-changed'))).catch(() => {});
});

const routes = () => (window.Theme && window.Theme.routes) || {};
const stripJs = (u) => String(u).replace(/\.js$/, '');
const cartAddUrl = () => stripJs(routes().cart_add_url || '/cart/add');
const cartUrl = () => stripJs(routes().cart_url || '/cart');

function cartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function getCart() {
  const res = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Cart fetch failed: ${res.status}`);
  return res.json();
}

/**
 * @param {Array<{id: number|string, quantity: number}>} items
 * @param {HTMLElement} [source]
 */
async function addItems(items, source) {
  const lines = items.map((i) => ({ id: Number(i.id), quantity: Number(i.quantity) || 1 }));
  const sections = cartSectionIds();
  const deferred = CartLinesUpdateEvent.createPromise();
  const target = source || document.body;

  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: lines.map((l) => ({ merchandiseId: String(l.id), quantity: l.quantity })),
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${cartAddUrl()}.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: lines, sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const data = await res.json();

    if (!res.ok || data.status) {
      const cart = await getCart().catch(() => null);
      deferred.resolve({
        cart: cart ? CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
        detail: { didError: true, items: cart ? cart.items : [], source: 'serea-cart' },
      });
      throw new Error(data.description || data.message || 'Could not add to cart');
    }

    const cart = await getCart();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        sections: data.sections,
        itemCount: lines.reduce((s, l) => s + l.quantity, 0),
        source: 'serea-cart',
        didError: false,
      },
    });

    // Show the result: open the theme's cart drawer (or go to the cart page if there is no drawer).
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!document.querySelector('cart-drawer-component')) {
      window.location.href = cartUrl();
    } else if (drawer && typeof drawer.open === 'function' && !drawer.isOpen && !(source && source.closest && source.closest('#cart-drawer'))) {
      drawer.open();
    }
    document.dispatchEvent(new CustomEvent('serea:cart-updated', { detail: { cart } }));
    return cart;
  } catch (err) {
    deferred.reject(err);
    throw err;
  }
}

window.SereaCart = { addItems, getCart };
document.dispatchEvent(new CustomEvent('serea:cart-ready'));

// Cart drawer upsell button (rendered by snippets/serea-cart-upsell.liquid)
document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-serea-upsell-add]') : null;
  if (!btn) return;
  event.preventDefault();
  if (btn.hasAttribute('disabled')) return;
  btn.setAttribute('disabled', '');
  const label = btn.textContent;
  btn.textContent = btn.dataset.loadingText || 'Adding…';
  try {
    await addItems([{ id: btn.dataset.variantId, quantity: 1 }], btn);
  } catch (e) {
    console.warn('[serea-cart] upsell add failed', e);
    btn.textContent = label;
    btn.removeAttribute('disabled');
  }
});